import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('elev802_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const detail = error.response?.data?.detail || '';
      localStorage.removeItem('elev802_token');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register' && window.location.pathname !== '/') {
        // Show a friendly toast for the "logged in elsewhere" case
        if (detail.toLowerCase().includes('another device')) {
          try { sessionStorage.setItem('elev802_logout_reason', detail); } catch { /* ignore */ }
        }
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
function getDeviceId() {
  let id = localStorage.getItem('elev802_device_id');
  if (!id) {
    id = 'dev_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('elev802_device_id', id);
  }
  return id;
}

function getDeviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android';
  if (/Mac/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows PC';
  return 'Browser';
}

export const authAPI = {
  register: (data) => api.post('/api/auth/register', { ...data, device_id: getDeviceId(), device_name: getDeviceName() }),
  login: (data) => api.post('/api/auth/login', { ...data, device_id: getDeviceId(), device_name: getDeviceName() }),
  me: () => api.get('/api/auth/me'),
  logout: () => api.post('/api/auth/logout'),
  devices: () => api.get('/api/auth/devices'),
  removeDevice: (deviceId) => api.delete(`/api/auth/devices/${deviceId}`),
  passwordResetCheck: (token) => api.get('/api/auth/password-reset/check', { params: { token } }),
  passwordResetConsume: (token, newPassword) => api.post('/api/auth/password-reset/consume', { token, new_password: newPassword }),
  passwordResetRequest: (email) => api.post('/api/auth/password-reset/request', { email }),
};

// Drills
export const drillsAPI = {
  list: (category) => api.get('/api/drills', { params: category ? { category } : {} }),
  get: (id) => api.get(`/api/drills/${id}`),
  create: (data) => api.post('/api/drills', data),
  update: (id, data) => api.put(`/api/drills/${id}`, data),
  delete: (id) => api.delete(`/api/drills/${id}`),
};

// Training Plans
export const plansAPI = {
  list: () => api.get('/api/plans'),
  current: () => api.get('/api/plans/current'),
  create: (data) => api.post('/api/plans', data),
  update: (id, data) => api.put(`/api/plans/${id}`, data),
  delete: (id) => api.delete(`/api/plans/${id}`),
};

// Progress
export const progressAPI = {
  complete: (data) => api.post('/api/progress/complete', data),
  weekly: (weekNumber) => api.get('/api/progress/weekly', { params: weekNumber ? { week_number: weekNumber } : {} }),
  stats: () => api.get('/api/progress/stats'),
  parentReport: (weekNumber) => api.get('/api/progress/parent-report', { params: weekNumber ? { week_number: weekNumber } : {} }),
  overrideEffort: (progressId, effortRating) => api.put('/api/progress/override-effort', { progress_id: progressId, effort_rating: effortRating }),
};

// Scores
export const scoresAPI = {
  daily: (weekNumber, dayNumber) => api.get('/api/scores/daily', { params: { ...(weekNumber && { week_number: weekNumber }), ...(dayNumber && { day_number: dayNumber }) } }),
  weekly: (weekNumber) => api.get('/api/scores/weekly', { params: weekNumber ? { week_number: weekNumber } : {} }),
  summary: () => api.get('/api/scores/summary'),
  badges: () => api.get('/api/scores/badges'),
};

// Profile
export const profileAPI = {
  saveOnboarding: (data) => api.put('/api/profile/onboarding', data),
  me: () => api.get('/api/profile/me'),
  update: (data) => api.put('/api/profile/update', data),
  uploadHeadshot: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/api/profile/headshot', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  player: (id) => api.get(`/api/profile/player/${id}`),
};

// Friends
export const friendsAPI = {
  add: (friendId) => api.post('/api/friends/add', { friend_id: friendId }),
  remove: (friendId) => api.delete(`/api/friends/remove/${friendId}`),
  list: () => api.get('/api/friends/list'),
  leaderboard: () => api.get('/api/friends/leaderboard'),
};

// Feedback
export const feedbackAPI = {
  create: (data) => api.post('/api/feedback', data),
  list: () => api.get('/api/feedback'),
  review: (id, data) => api.put(`/api/feedback/${id}/review`, data),
};

// Upload — via backend to Cloudflare R2
const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks

export const uploadAPI = {
  video: async (file, onProgress) => {
    const sizeMB = (file.size / 1024 / 1024).toFixed(1);
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    console.log(`[Upload] ${file.name}, ${sizeMB}MB, ${totalChunks} chunks`);

    // Step 1: Get upload session from backend
    let uploadId;
    try {
      const fd = new FormData();
      fd.append('filename', file.name || 'video.mp4');
      fd.append('total_chunks', totalChunks.toString());
      const res = await api.post('/api/upload/init', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      uploadId = res.data.upload_id;
    } catch (err) {
      throw new Error('Could not start upload. Try again.');
    }

    // Step 2: Send chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);

      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', i.toString());
      fd.append('file', blob, `chunk_${i}`);

      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          await api.post('/api/upload/chunk', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
          });
          break;
        } catch (err) {
          if (attempt === 3) throw new Error('Upload failed — check your connection');
          await new Promise(r => setTimeout(r, 2000 * attempt));
        }
      }

      if (onProgress) onProgress({ loaded: end, total: file.size });
    }

    // Step 3: Complete — backend assembles and uploads to R2
    const result = await api.post('/api/upload/complete', (() => {
      const fd = new FormData();
      fd.append('upload_id', uploadId);
      return fd;
    })(), {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });

    return result;
  },
};

// Admin
export const adminAPI = {
  players: (birthYear, showIncomplete, showInactive) => api.get('/api/admin/players', { params: { birth_year: birthYear || 'all', show_incomplete: showIncomplete || false, show_inactive: showInactive || false } }),
  playerDetail: (id) => api.get(`/api/admin/player/${id}`),
  updatePlayerProfile: (id, data) => api.put(`/api/admin/player/${id}/profile`, data),
  updateSubscription: (userId, active) => api.put(`/api/admin/subscription/${userId}`, { subscription_active: active }),
  deletePlayer: (userId) => api.delete(`/api/admin/players/${userId}`),
  removePlayerDevice: (userId, deviceId) => api.delete(`/api/admin/player/${userId}/device/${deviceId}`),
  resetPlayer: (userId) => api.post(`/api/admin/player/${userId}/reset`),
  setPlayerStreak: (userId, current, longest) => api.post(`/api/admin/player/${userId}/streak`, { current, longest }),
  setPlayerWeeklyStreak: (userId, weekly) => api.post(`/api/admin/player/${userId}/weekly-streak`, { weekly }),
  createPasswordReset: (userId) => api.post(`/api/admin/player/${userId}/reset-password`),
  realignTrainingDates: () => api.post('/api/admin/realign-training-start-dates'),
  smsAudience: () => api.get('/api/messages/sms/audience'),
  smsBroadcast: (content) => api.post('/api/messages/sms/broadcast', { content }),
  smsMissingBirthYearAudience: () => api.get('/api/messages/sms/missing-birth-year/audience'),
  smsMissingBirthYearSend: () => api.post('/api/messages/sms/missing-birth-year'),
  reconcileStripe: () => api.post('/api/admin/reconcile-stripe-subscriptions'),
  backfillFoundersBadges: () => api.post('/api/admin/backfill-founders-badges'),
  warmupVideos: () => api.post('/api/admin/warmup-videos', {}, { timeout: 600000 }),
  sendWeeklyRecap: () => api.post('/api/admin/send-weekly-recap'),
  sendSmartNotifications: () => api.post('/api/admin/send-smart-notifications'),
  analytics: () => api.get('/api/admin/analytics'),
  realtimeStats: () => api.get('/api/admin/realtime-stats'),
  clearPhantomDevices: () => api.post('/api/admin/clear-phantom-devices'),
  flaggedPlayers: () => api.get('/api/admin/flagged-players'),
};

// Push Notifications
export const pushAPI = {
  getVapidKey: () => api.get('/api/push/vapid-key'),
  subscribe: (subscription) => api.post('/api/push/subscribe', subscription),
  unsubscribe: () => api.post('/api/push/unsubscribe'),
};

// Leaderboard
export const leaderboardAPI = {
  weekly: (ageGroup, gender) => api.get('/api/leaderboard/weekly', { params: { ...(ageGroup && { age_group: ageGroup }), ...(gender && { gender }) } }),
  streaks: (ageGroup, gender) => api.get('/api/leaderboard/streaks', { params: { ...(ageGroup && { age_group: ageGroup }), ...(gender && { gender }) } }),
  scores: (ageGroup, gender) => api.get('/api/leaderboard/scores', { params: { ...(ageGroup && { age_group: ageGroup }), ...(gender && { gender }) } }),
  coachView: () => api.get('/api/leaderboard/coach-view'),
};

// Messages
export const messagesAPI = {
  conversations: () => api.get('/api/messages/conversations'),
  thread: (otherId) => api.get(`/api/messages/thread/${otherId}`),
  send: (receiverId, content) => api.post('/api/messages/send', { receiver_id: receiverId, content }),
  unreadCount: () => api.get('/api/messages/unread-count'),
  coaches: () => api.get('/api/messages/coaches'),
  players: () => api.get('/api/messages/players'),
};

// Featured Videos (landing page)
export const featuredAPI = {
  getAll: () => axios.get(`${API_URL}/api/featured-videos`),
  set: (slot, data) => api.put(`/api/featured-videos/${slot}`, data),
  remove: (slot) => api.delete(`/api/featured-videos/${slot}`),
};

// Payments
export const paymentsAPI = {
  config: () => api.get('/api/payments/config'),
  checkout: (originUrl) => api.post('/api/payments/checkout', { origin_url: originUrl }),
  portal: (returnUrl) => api.post('/api/payments/portal', { return_url: returnUrl }),
  status: (sessionId) => api.get(`/api/payments/status/${sessionId}`),
};

// Library
export const libraryAPI = {
  weeks: () => api.get('/api/library/weeks'),
  weekDetail: (weekNum) => api.get(`/api/library/week/${weekNum}`),
  unlocked: () => api.get('/api/library/unlocked'),
};

export const challengesAPI = {
  // Coach
  list: () => api.get('/api/challenges'),
  create: (data) => api.post('/api/challenges', data),
  update: (id, data) => api.put(`/api/challenges/${id}`, data),
  delete: (id) => api.delete(`/api/challenges/${id}`),
  // Player
  current: () => api.get('/api/challenges/current'),
  submit: (data) => api.post('/api/challenges/submit', data),
  mySubmissions: () => api.get('/api/challenges/my-submissions'),
  // Coach review
  allSubmissions: (status) => api.get('/api/challenges/submissions', { params: status ? { status } : {} }),
  challengeSubmissions: (challengeId) => api.get(`/api/challenges/${challengeId}/submissions`),
  reviewSubmission: (submissionId, data) => api.post(`/api/challenges/submissions/${submissionId}/feedback`, data),
};

// Favorites
export const favoritesAPI = {
  toggle: (data) => api.post('/api/favorites/toggle', data),
  list: () => api.get('/api/favorites'),
};

export default api;
